import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { VaultLogoMini } from "@/components/VaultLogo";
import { LogOut, RefreshCw, Loader2, Mail, Phone, Calendar, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tgv_admin_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const LoginForm = ({ onAuthed }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Enter both username and password");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Welcome back");
      onAuthed(data.access_token);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center px-6">
      <form
        onSubmit={onSubmit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-[#111111] border border-[#C9960C]/40 border-t-2 border-t-[#C9960C] p-10"
      >
        <div className="flex flex-col items-center mb-8">
          <VaultLogoMini size={44} />
          <p className="mt-4 text-[11px] tracking-[0.32em] uppercase text-[#C9960C]">
            Private Console
          </p>
          <h1 className="font-serif-display text-[#F5D485] text-3xl mt-2 tracking-tight">
            Admin Sign In
          </h1>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#C9960C] mb-2">
              Username
            </label>
            <input
              data-testid="admin-username-input"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-transparent border-b border-[#C9960C]/40 text-[#F8F6F0] focus:border-[#C9960C] focus:outline-none py-3 px-0 transition-colors"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#C9960C] mb-2">
              Password
            </label>
            <input
              data-testid="admin-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-[#C9960C]/40 text-[#F8F6F0] focus:border-[#C9960C] focus:outline-none py-3 px-0 transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#080808] border border-[#C9960C] text-[#C9960C] px-8 py-4 uppercase tracking-[0.22em] text-xs font-medium hover:bg-[#C9960C] hover:text-[#080808] transition-colors duration-300 cursor-pointer disabled:opacity-60"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
            Sign In
          </button>
        </div>
      </form>
    </div>
  );
};

const EnquiriesTable = ({ token, onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries?limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
        return;
      }
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const filtered = items.filter((it) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      it.name?.toLowerCase().includes(q) ||
      it.email?.toLowerCase().includes(q) ||
      it.phone?.toLowerCase().includes(q) ||
      it.message?.toLowerCase().includes(q)
    );
  });

  const downloadCsv = () => {
    const rows = [
      ["Name", "Email", "Phone", "Message", "Received At"],
      ...filtered.map((it) => [it.name, it.email, it.phone, it.message, it.created_at]),
    ];
    const csv = rows
      .map((r) =>
        r
          .map((v) => {
            const s = String(v ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#080808] text-[#F8F6F0]">
      {/* Header */}
      <header className="border-b border-[#C9960C]/30 bg-[#080808]/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <VaultLogoMini size={34} />
            <div className="leading-tight">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#C9960C]">Private Console</p>
              <p className="font-serif-display text-[#F5D485] text-lg tracking-widest-extra uppercase">
                The Global Vault
              </p>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 border border-[#C9960C] text-[#C9960C] px-4 py-2 uppercase tracking-[0.2em] text-[11px] hover:bg-[#C9960C] hover:text-[#080808] transition-colors cursor-pointer"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#C9960C] mb-3">Enquiries</p>
            <h1 className="font-serif-display text-[#F5D485] text-4xl sm:text-5xl tracking-tight">
              All Received Enquiries
            </h1>
            <p className="mt-3 text-[#F8F6F0]/70">
              <span data-testid="enquiries-count">{filtered.length}</span> of {items.length} shown
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <input
              data-testid="enquiries-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, phone…"
              className="bg-[#111111] border border-[#C9960C]/40 text-[#F8F6F0] focus:border-[#C9960C] focus:outline-none px-4 py-2.5 text-sm min-w-[260px]"
            />
            <button
              onClick={fetchItems}
              data-testid="enquiries-refresh"
              className="inline-flex items-center gap-2 border border-[#C9960C] text-[#C9960C] px-4 py-2.5 uppercase tracking-[0.2em] text-[11px] hover:bg-[#C9960C] hover:text-[#080808] transition-colors cursor-pointer"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={downloadCsv}
              disabled={!filtered.length}
              data-testid="enquiries-export"
              className="inline-flex items-center gap-2 border border-[#C9960C] text-[#C9960C] px-4 py-2.5 uppercase tracking-[0.2em] text-[11px] hover:bg-[#C9960C] hover:text-[#080808] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        <div
          data-testid="enquiries-table-wrap"
          className="bg-[#111111] border border-[#C9960C]/30 border-t-2 border-t-[#C9960C] overflow-hidden"
        >
          {loading ? (
            <div className="py-20 flex items-center justify-center text-[#C9960C]">
              <Loader2 size={20} className="animate-spin mr-2" /> Loading enquiries…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-[#F8F6F0]/60">
              No enquiries {query ? "match your search" : "yet"}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="enquiries-table">
                <thead>
                  <tr className="text-left text-[10px] tracking-[0.3em] uppercase text-[#C9960C] border-b border-[#C9960C]/30">
                    <th className="px-5 py-4 font-medium">Received</th>
                    <th className="px-5 py-4 font-medium">Name</th>
                    <th className="px-5 py-4 font-medium">Email</th>
                    <th className="px-5 py-4 font-medium">Phone</th>
                    <th className="px-5 py-4 font-medium">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr
                      key={it.id}
                      data-testid={`enquiry-row-${it.id}`}
                      className="border-b border-[#C9960C]/10 hover:bg-[#1C1C1C]/60 transition-colors align-top"
                    >
                      <td className="px-5 py-4 text-[#F8F6F0]/70 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar size={12} className="text-[#C9960C]/60" />
                          {formatDate(it.created_at)}
                        </div>
                      </td>
                      <td className="px-5 py-4 font-serif-display text-[#F5D485] text-base whitespace-nowrap">
                        {it.name}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <a
                          href={`mailto:${it.email}`}
                          className="inline-flex items-center gap-2 text-[#F8F6F0]/85 hover:text-[#C9960C]"
                        >
                          <Mail size={12} className="text-[#C9960C]/60" />
                          {it.email}
                        </a>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <a
                          href={`tel:${it.phone}`}
                          className="inline-flex items-center gap-2 text-[#F8F6F0]/85 hover:text-[#C9960C]"
                        >
                          <Phone size={12} className="text-[#C9960C]/60" />
                          {it.phone}
                        </a>
                      </td>
                      <td className="px-5 py-4 text-[#F8F6F0]/80 max-w-md">
                        <div className="whitespace-pre-wrap break-words leading-relaxed">
                          {it.message}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginForm onAuthed={(t) => setToken(t)} />;
  return <EnquiriesTable token={token} onLogout={handleLogout} />;
};

export default AdminPage;
